import ShareDialog from "@/components/Chat/messages/components/ShareDialog";
import { cn } from "@/lib/utils";
import {
  getOptimizedImageUrl,
  ImagePresets,
} from "@/lib/utils/supabaseImageTransform";
import {
  fetchArticleBySlug,
  fetchRelatedArticles,
} from "@/services/newsService";
import { ArrowLeft, Clock, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

// Content blocks renderer
const ContentBlock = ({ block }) => {
  switch (block.type) {
    case "paragraph":
      return (
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{block.data.text}</p>
      );

    case "heading": {
      const HeadingTag = `h${block.data.level || 2}`;
      const headingClasses = {
        1: "text-4xl font-bold text-gray-900 dark:text-white mb-8",
        2: "text-3xl font-semibold text-gray-900 dark:text-white mb-6 mt-12",
        3: "text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8",
        4: "text-xl font-semibold text-gray-900 dark:text-white mb-4 mt-6",
      };

      return (
        <HeadingTag className={headingClasses[block.data.level || 2]}>
          {block.data.text}
        </HeadingTag>
      );
    }

    case "quote":
      return (
        <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-gray-600 dark:text-gray-300 text-lg">
          {block.data.text}
          {block.data.attribution && (
            <cite className="block mt-2 text-sm text-gray-500 dark:text-gray-400 not-italic">
              — {block.data.attribution}
            </cite>
          )}
        </blockquote>
      );

    case "list": {
      const ListTag = block.data.style === "ordered" ? "ol" : "ul";
      const listClasses =
        block.data.style === "ordered"
          ? "list-decimal list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300"
          : "list-disc list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300";

      return (
        <ListTag className={listClasses}>
          {block.data.items.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {item}
            </li>
          ))}
        </ListTag>
      );
    }

    case "callout":
      return (
        <div
          className={cn(
            "p-6 rounded-lg border-l-4 mb-8",
            block.data.type === "warning" &&
              "bg-yellow-100 dark:bg-yellow-900/20 border-yellow-500",
            block.data.type === "info" && "bg-blue-100 dark:bg-blue-900/20 border-blue-500",
            block.data.type === "success" && "bg-green-100 dark:bg-green-900/20 border-green-500",
            !block.data.type && "bg-gray-100 dark:bg-gray-800/50 border-gray-400 dark:border-gray-600"
          )}
        >
          {block.data.title && (
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              {block.data.title}
            </h4>
          )}
          <p className="text-gray-600 dark:text-gray-300">{block.data.text}</p>
        </div>
      );

    case "divider":
      return <hr className="border-gray-300 dark:border-gray-700 my-12" />;

    case "image":
      return (
        <figure className="my-8">
          <img
            src={getOptimizedImageUrl(block.data.url, ImagePresets.fullsize)}
            alt={block.data.alt}
            className="w-full rounded-lg"
            loading="lazy"
            decoding="async"
          />
          {block.data.caption && (
            <figcaption className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
              {block.data.caption}
            </figcaption>
          )}
        </figure>
      );

    default:
      return null;
  }
};

export default function ArticlePage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [error, setError] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Function to convert content blocks to plain text
  const convertContentToText = (content) => {
    if (!content) return "";

    // If it's already a string, return it
    if (typeof content === "string") return content;

    // If it has markdown property, return that
    if (content.markdown) return content.markdown;

    // If it has html property, strip HTML tags
    if (content.html) {
      return content.html.replace(/<[^>]*>/g, "");
    }

    // If it has blocks, convert them to text
    if (content.blocks && Array.isArray(content.blocks)) {
      return content.blocks
        .map((block) => {
          switch (block.type) {
            case "paragraph":
              return block.data.text || "";
            case "heading":
              return block.data.text || "";
            case "quote":
              return block.data.text || "";
            case "list":
              return block.data.items ? block.data.items.join(", ") : "";
            case "callout":
              return block.data.text || "";
            default:
              return "";
          }
        })
        .filter((text) => text.length > 0)
        .join("\n\n");
    }

    return "";
  };

  useEffect(() => {
    const loadArticle = async () => {
      setIsLoading(true);
      setError(null);

      const { data, error } = await fetchArticleBySlug(articleId);

      if (error) {
        console.error("Error loading article:", error);
        setError("Article not found or failed to load.");
        setArticle(null);
      } else {
        setArticle(data);

        // Load related articles
        if (data && data.relatedArticles && data.relatedArticles.length > 0) {
          setIsLoadingRelated(true);
          const { data: related } = await fetchRelatedArticles(
            data.id,
            data.relatedArticles
          );
          setRelatedArticles(related || []);
          setIsLoadingRelated(false);
        } else if (data) {
          // Fetch articles from the same category if no specific related articles
          setIsLoadingRelated(true);
          const { data: related } = await fetchRelatedArticles(data.id, [], 2);
          setRelatedArticles(related || []);
          setIsLoadingRelated(false);
        }
      }

      setIsLoading(false);
    };

    if (articleId) {
      loadArticle();
    }
  }, [articleId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white">
        {/* Navigation */}
        <nav className="px-6 py-4 md:px-12 border-b border-gray-200 dark:border-gray-800">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </nav>

        {/* Loading State */}
        <div className="px-6 py-12 md:px-12 max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
              <div className="w-16 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="w-3/4 h-12 bg-gray-300 dark:bg-gray-700 rounded mb-8"></div>
            <div className="w-full h-96 bg-gray-300 dark:bg-gray-700 rounded-lg mb-8"></div>
            <div className="space-y-4">
              <div className="w-full h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="w-full h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="w-2/3 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white">
        {/* Navigation */}
        <nav className="px-6 py-4 md:px-12 border-b border-gray-200 dark:border-gray-800">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </nav>

        {/* Error State */}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              {error || "The article you're looking for doesn't exist."}
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to News
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white">
      {/* Navigation */}
      <nav className="px-6 py-4 md:px-12 border-b border-gray-200 dark:border-gray-800">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to News
        </Link>
      </nav>

      {/* Article Header */}
      <header className="px-6 py-12 md:px-12 max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6 text-sm text-gray-500 dark:text-gray-400">
          <span>{formatDate(article.date)}</span>
          <span>•</span>
          <span>{article.category}</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-8">
          {article.title}
        </h1>

        {/* Hero Image */}
        <div className="relative mb-8">
          <img
            src={getOptimizedImageUrl(
              article.image?.url ||
                "/placeholder.svg?height=400&width=800&text=Article+Image",
              { width: 1200, height: 600, quality: 80, resize: "cover" }
            )}
            alt={article.image?.alt || article.title}
            className="w-full h-[400px] object-cover rounded-lg"
            loading="eager"
            decoding="async"
          />

          {/* Share Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShareDialogOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-transparent border border-gray-900 dark:border-white text-gray-900 dark:text-white hover:bg-gray-900 dark:hover:bg-white hover:text-white dark:hover:text-black rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Article Meta */}
        <div className="flex items-center justify-between py-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden">
              <img
                src={getOptimizedImageUrl(
                  "/blog/jack-image.jpeg",
                  ImagePresets.avatar
                )}
                alt={article.author?.name || "Author"}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div>
              <p className="font-medium">
                {article.author?.name || "Anonymous"}
              </p>
              {article.author?.title && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{article.author.title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>{article.readTime}</span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="px-6 md:px-12 max-w-4xl mx-auto pb-16">
        <div className="prose prose-gray dark:prose-invert prose-lg max-w-none">
          {article.content?.blocks?.map((block, index) => (
            <ContentBlock key={index} block={block} />
          ))}

          {/* Fallback for HTML content */}
          {!article.content?.blocks && article.content?.html && (
            <div
              className="prose prose-gray dark:prose-invert prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content.html }}
            />
          )}

          {/* Markdown content rendering */}
          {!article.content?.blocks &&
            !article.content?.html &&
            article.content?.markdown && (
              <div className="prose prose-gray dark:prose-invert prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 mt-12">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6 mt-12">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 mt-6">
                        {children}
                      </h4>
                    ),
                    p: ({ children }) => (
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="leading-relaxed">{children}</li>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-gray-600 dark:text-gray-300 text-lg">
                        {children}
                      </blockquote>
                    ),
                    code: ({ inline, children }) =>
                      inline ? (
                        <code className="bg-gray-200 dark:bg-gray-800 px-2 py-1 rounded text-sm text-gray-700 dark:text-gray-300">
                          {children}
                        </code>
                      ) : (
                        <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto mb-6">
                          <code className="text-gray-700 dark:text-gray-300">{children}</code>
                        </pre>
                      ),
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                    img: ({ src, alt }) => (
                      <figure className="my-8">
                        <img
                          src={getOptimizedImageUrl(src, ImagePresets.fullsize)}
                          alt={alt}
                          className="w-full rounded-lg"
                          loading="lazy"
                          decoding="async"
                        />
                        {alt && (
                          <figcaption className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                            {alt}
                          </figcaption>
                        )}
                      </figure>
                    ),
                    hr: () => <hr className="border-gray-300 dark:border-gray-700 my-12" />,
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900 dark:text-white">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-gray-600 dark:text-gray-300">{children}</em>
                    ),
                  }}
                >
                  {article.content.markdown}
                </ReactMarkdown>
              </div>
            )}

          {/* Fallback for plain text content */}
          {!article.content?.blocks &&
            !article.content?.html &&
            !article.content?.markdown &&
            article.content && (
              <div className="prose prose-gray dark:prose-invert prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 mt-12">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6 mt-12">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                        {children}
                      </h3>
                    ),
                    h4: ({ children }) => (
                      <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 mt-6">
                        {children}
                      </h4>
                    ),
                    p: ({ children }) => (
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-300">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="leading-relaxed">{children}</li>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-blue-500 pl-6 my-8 italic text-gray-600 dark:text-gray-300 text-lg">
                        {children}
                      </blockquote>
                    ),
                    code: ({ inline, children }) =>
                      inline ? (
                        <code className="bg-gray-200 dark:bg-gray-800 px-2 py-1 rounded text-sm text-gray-700 dark:text-gray-300">
                          {children}
                        </code>
                      ) : (
                        <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto mb-6">
                          <code className="text-gray-700 dark:text-gray-300">{children}</code>
                        </pre>
                      ),
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                    img: ({ src, alt }) => (
                      <figure className="my-8">
                        <img
                          src={getOptimizedImageUrl(src, ImagePresets.fullsize)}
                          alt={alt}
                          className="w-full rounded-lg"
                          loading="lazy"
                          decoding="async"
                        />
                        {alt && (
                          <figcaption className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                            {alt}
                          </figcaption>
                        )}
                      </figure>
                    ),
                    hr: () => <hr className="border-gray-300 dark:border-gray-700 my-12" />,
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900 dark:text-white">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-gray-600 dark:text-gray-300">{children}</em>
                    ),
                  }}
                >
                  {typeof article.content === "string" ? article.content : ""}
                </ReactMarkdown>
              </div>
            )}
        </div>

        {/* Related Articles */}
        {(relatedArticles.length > 0 || isLoadingRelated) && (
          <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
            <h3 className="text-2xl font-semibold mb-6">Related Articles</h3>

            {isLoadingRelated ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className="p-6 bg-gray-100 dark:bg-gray-900 rounded-lg animate-pulse"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-16 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                      <div className="w-2 h-2 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                      <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                    </div>
                    <div className="w-3/4 h-6 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="w-full h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedArticles.map((relatedArticle) => (
                  <Link
                    key={relatedArticle.id}
                    to={`/news/${relatedArticle.slug}`}
                    className="group block p-6 bg-gray-100 dark:bg-gray-900 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-3 text-sm text-gray-500 dark:text-gray-400">
                      <span>{relatedArticle.category}</span>
                      <span>•</span>
                      <span>{formatDate(relatedArticle.date)}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
                      {relatedArticle.title}
                    </h4>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 line-clamp-2">
                      {relatedArticle.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        content={
          convertContentToText(article?.content) || article?.excerpt || ""
        }
        title={article?.title || "Article"}
      />
    </div>
  );
}
